import React,{useEffect, useState} from "react";
import {Box, Button, Grid, IconButton, Paper, Stack, Tab, Tabs, useTheme} from "@mui/material";
import {CreateRounded} from "@mui/icons-material";
import Tooltip from "@mui/material/Tooltip";
import {useTranslation} from "react-i18next";
import {Form, FormikHelpers, FormikProvider, useFormik, useFormikContext, yupToFormErrors} from "formik";
import {useRecoilState, useRecoilValue} from "recoil";
import {FormValuesType, IRFMContractorDataForm} from "../model/model";
import {CONTRACTOR_DATA_FORM_CUSTOM_VALUES, CONTRACTOR_DATA_FORM_INITIAL_VALUE, CONTRACTOR_DATA_FORM_VALIDATION_SCHEMA} from "../utils/config";
import {buttonConfirmedState, entityTerminatedState, formEditingState, isSubmitState, newRegistrationState, openDialogState, RFMContractorDataValueState, RFMState} from "../model/state";
import ContractorManagementView from "../component/ContractorManagementView";
import CurrentYearDashboardTab from "./CurrentYearDashboardTab";
import DocumentRepository from "./DocumentRepository";
import AssociationContractorDms from "./AssociationContractorDms";

/*
* Il componente mantiene i dati di tutti i componenti figli
* Tutti i field vengono passati al check, una volta validati si procederà ad attivare il bottone per il salvataggio della form.
* Terminate funge da delete a DB
* Quando la form viene salvata tutti i tab si disattivano
*/

const RoyaltiesFeesManagementTab = () => {
    let initPage: number = 0;
    const theme = useTheme();
    const {t} = useTranslation(['network', 'translation']);
    const handleDisableForm = () => {
        editingForm === 0 ? changeEnabledFormState() : changeDisabledFormState()
    }
    const [tabValue, setTabValue] = useState(0);

    // Use Formik Context
    const {values} = useFormikContext<FormValuesType>();

    const rfmState = useRecoilValue(RFMState)

    const [getContractorDataState, setDmsState] = useRecoilState(RFMContractorDataValueState);
    const [isNewReg, ] = useRecoilState(newRegistrationState);
    const [getEntityTerminatedState, ] = useRecoilState(entityTerminatedState);

    const [isSubmit, setIsSubmit] = useRecoilState(isSubmitState);
    const [, setButtonConfirm] = useRecoilState(buttonConfirmedState);

    const [editingForm, setFormEditingState] = useRecoilState(formEditingState);

    const changeEnabledFormState = () => setFormEditingState(1);
    const changeDisabledFormState = () => setFormEditingState(0);

    const [confirmDialogOpen, setConfirmDialogOpen] = useRecoilState(openDialogState);

    /************************************************/
    /*************** CONTRALLER BUTTON FORM ***************/
    /************************************************/
    const handleClick = (event: React.SyntheticEvent, nameButton: string) => {
        setIsSubmit(true);
        setButtonConfirm(nameButton);
        if(!confirmDialogOpen && nameButton !== 'modify') {
            if (nameButton === 'reset') {
                contractorDataForm.resetForm({
                    values: CONTRACTOR_DATA_FORM_INITIAL_VALUE});
                //contractorDataForm.setFieldValue('contractorData', CONTRACTOR_DATA_FORM_INITIAL_VALUE);
                changeEnabledFormState();
            }
            if (nameButton === 'confirm') {
                setDmsState(contractorDataForm.values.contractorData);
                setConfirmDialogOpen(true);
            }
            if (nameButton === 'terminate') {
                setConfirmDialogOpen(true);
                editingForm ? changeDisabledFormState() : changeEnabledFormState();
            }
        }
        if (nameButton === 'modify') {
            editingForm ? changeDisabledFormState() : changeEnabledFormState();
        }
        setIsSubmit(false);
    };

    //Tab State
    const handleChange = (event: React.SyntheticEvent, newValue: any) => {
        setTabValue(newValue);
    };

    useEffect(() => {
            if (initPage === 0) {
                initPage = 1;
                if (!isNewReg)
                    contractorDataForm.setFieldValue('contractorData', getContractorDataState)
            }
    }, [values, rfmState]);

    /************************************************/
    /*************** FORMIK DATA FORM ***************/
    /************************************************/
    const contractorDataForm = useFormik<IRFMContractorDataForm>({
        initialValues: !isNewReg ? getContractorDataState : CONTRACTOR_DATA_FORM_CUSTOM_VALUES,
        onSubmit: (values: IRFMContractorDataForm, actions: FormikHelpers<IRFMContractorDataForm>) => { // era presente action: IcontractorDataForm
            actions.setSubmitting(false)
            // TODO: add action on submit
        },
        validate: async (values) => {
            return CONTRACTOR_DATA_FORM_VALIDATION_SCHEMA
                .validate(values, {
                    abortEarly: false,
                    context: {}
                })
                .then(() => {
                    return {};
                })
                .catch(err => {
                    return yupToFormErrors(err);
                });
        },
        validationSchema: CONTRACTOR_DATA_FORM_VALIDATION_SCHEMA,
        validateOnChange: true,
        validateOnBlur: false
    });

    return (
        <Paper elevation={1} sx={{p: theme.spacing(2), mb: theme.spacing(2)}}>

            <FormikProvider value={contractorDataForm}>
                <Form>
                    <Box>
                        <Box sx={{ width: '100%', mb: theme.spacing(2)}}>
                            <Box>
                                <Tabs value={tabValue} onChange={handleChange} >
                                    <Tab label={t('contractorMng.managerRFM.royalitiesFeesManagementTab.tabs.generalInfo')}/>
                                    <Tab label={t('contractorMng.managerRFM.royalitiesFeesManagementTab.tabs.documentRepository')}/>
                                    <Tab label={t('contractorMng.managerRFM.royalitiesFeesManagementTab.tabs.dashboard')} />
                                    <Tab label={t('contractorMng.managerRFM.royalitiesFeesManagementTab.tabs.association')}/>
                                </Tabs>
                            </Box>

                            {/* DMS Registration */}
                            {tabValue === 0 &&
                                <Box role={'tabpanel'}>
                                    <Grid item xs={12}>
                                    <Stack direction="column" spacing={2} justifyContent="flex-end" alignItems="flex-end">
                                        { editingForm === 0 && (
                                        <Tooltip title={t('royaltiesFeesManagement.contractorData.button.edit')}>
                                            <IconButton sx={{mx: theme.spacing(0.5)}} color={"accent"}
                                                        onClick={() => {handleDisableForm()}}>
                                                <CreateRounded />
                                            </IconButton>
                                        </Tooltip>
                                        ) }
                                    </Stack>
                                </Grid>
                                    <ContractorManagementView
                                        key={'royaltiesFeesManagement'}
                                        title={t('royaltiesFeesManagement.contractorData.title')}
                                        referenceProp={'contractorData'}
                                    />
                                    {
                                        editingForm != 0 &&
                                        <Grid container item mt={1} justifyContent="end" columnGap={2}>
                                            {/*<Button  onClick={(event) => {*/}
                                            {/*    handleClick(event,'reset');*/}
                                            {/*}} disabled={isSubmit ? true : isNewReg === 0}>*/}
                                            {/*    Reset*/}
                                            {/*</Button>*/}
                                            <Button
                                                onClick={(event) => {handleClick(event, 'confirm');}}
                                                disabled={isSubmit || editingForm === 0}
                                            >
                                                {t("contractorMng.managerRFM.royalitiesFeesManagementTab.buttons.confirm")}
                                            </Button>
                                            {/*<Button  onClick={(event) => {*/}
                                            {/*    handleClick(event,'modify');*/}
                                            {/*}} disabled={isSubmit && isNewReg === 1 }>*/}
                                            {/*    {editingForm ? 'Lock Modify' : 'Enable Modify'}*/}
                                            {/*</Button>*/}
                                            {/* TODO: termina l'entità DMS sui sistemi quando è nello stato Finale o già In Progress != da NewRegistration*/}
                                            <Button onClick={(event) => { handleClick(event, 'terminate'); }} disabled={isNewReg === 1 ? true : !!getEntityTerminatedState}>
                                                {t("contractorMng.managerRFM.royalitiesFeesManagementTab.buttons.terminate")}
                                            </Button>
                                        </Grid>
                                    }
                                </Box>
                            }
                            {/* Activated when first tab is completed */}
                            {/* TODO: inserimento/estrazione di tutti i documenti nel repository */}
                            {/* Document Repository DMS */ }
                            {tabValue === 1 &&
                                <Box role={'tabpanel'}>
                                    <DocumentRepository/>
                                </Box>
                            }

                            {/* TODO: dashboard */}
                            {/* Activated when first tab is completed */}
                            {/* Dashboard */}
                            {tabValue === 2 &&
                                <Box role={'tabpanel'}>
                                    <CurrentYearDashboardTab />
                                </Box>
                            }
                            {/* Association */}
                            {tabValue === 3 &&
                                <Box role={'tabpanel'}>
                                    <AssociationContractorDms />
                                </Box>
                            }
                        </Box>

                    </Box>
                </Form>
            </FormikProvider>
        </Paper>
    )
}

export default RoyaltiesFeesManagementTab;